/**
 * The data-subject request workspace: assisted export, correction and closure (doc 22 §3.3).
 *
 * Devotees self-serve export and closure in the app. This workspace exists for requests that
 * arrive by phone, email, letter or the web form — and everything about it is built so an operator
 * cannot become the subject's proxy:
 *   - **The pipeline verifies identity, not the operator.** A closure moves only when the principal
 *     reads back a code sent to their own registered phone. Vouching is not permitted.
 *   - **The export never lands here.** The server queues it and tells the principal; no response
 *     this screen receives carries a link, a key or a byte of the archive.
 *   - **A blocked closure queues, it never fails silently,** and every blocker is a link to the
 *     screen where it can actually be cleared.
 *   - **Retention is shown in full, always** — the list comes from the server, so this screen and
 *     the policy cannot drift. So does the grace setting's KEY (E69: this screen once named
 *     `retention.closure_grace_days`, a setting that has never existed).
 */
import { useState } from 'react';
import { Button, Icon } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { DataTable } from '../components/data';
import { KV, Timeline, type TimelineEvent } from '../components/entities';
import { ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useApplyCorrection,
  useCancelDsr,
  useDsrWorkspace,
  useRequestClosure,
  useRequestExport,
  useVerifyClosure,
  type AssistedChannel,
  type DsrRequest,
} from '../lib/api/hooks/dsr';
import { formatDateTime } from '../lib/datetime';
import { count } from '../lib/text';

const KINDS = ['closure', 'export', 'correction'] as const;
type Kind = (typeof KINDS)[number];
const CHANNELS: readonly AssistedChannel[] = ['phone', 'email', 'web_form', 'letter'];
const PROFILE_FIELDS = ['full_name', 'gotra', 'preferred_language'] as const;
const FAMILY_FIELDS = ['full_name', 'relation', 'gotra', 'nakshatra', 'rashi', 'date_of_birth', 'birth_place'] as const;
const CANCELLABLE = new Set(['pending', 'awaiting_identity', 'blocked', 'grace']);

/** Where each kind of blocker is cleared. Exported for its own test. */
export const BLOCKER_TARGET: Record<string, string> = {
  booking: 'dispatch',
  refund: 'refunds',
  dispute: 'disputes',
};

/** How a request reads on the history timeline. Exported for its own test. */
export function requestState(status: string): TimelineEvent['state'] {
  if (status === 'completed' || status === 'delivered') return 'done';
  if (status === 'failed' || status === 'incident') return 'error';
  if (status === 'cancelled' || status === 'expired') return 'future';
  return 'current';
}

/** One line of detail for the history — names and references, never values. */
export function requestDetail(r: DsrRequest): string {
  const parts = [r.ticket_code ? `Ticket ${r.ticket_code}` : 'In the app', r.channel];
  if (r.fields_changed && r.fields_changed.length) parts.push(r.fields_changed.join(', '));
  if (r.blockers && r.blockers.length) parts.push(`${r.blockers.length} open matters`);
  if (r.grace_ends_at) parts.push(`grace ends ${formatDateTime(r.grace_ends_at)}`);
  if (r.failure_reason) parts.push(r.failure_reason);
  return parts.join(' · ');
}

export function DsrWorkspace() {
  const { scope, go, params, toast } = useConsole();
  const userId = Number(params.userId);
  /* A malformed id still asks: the server's refusal renders as the error state, not a spinner. */
  const workspace = useDsrWorkspace(userId);
  const requestExport = useRequestExport(userId);
  const applyCorrection = useApplyCorrection(userId);
  const requestClosure = useRequestClosure(userId);
  const verifyClosure = useVerifyClosure(userId);
  const cancelDsr = useCancelDsr(userId);

  const [kind, setKind] = useState<Kind>('closure');
  const [channel, setChannel] = useState<AssistedChannel>('phone');
  const [ticket, setTicket] = useState('');
  const [confirm, setConfirm] = useState<string | null>(null);
  const [target, setTarget] = useState('profile');
  const [field, setField] = useState<string>('gotra');
  const [value, setValue] = useState('');
  const [code, setCode] = useState('');
  const [cancelling, setCancelling] = useState<number | null>(null);

  if (workspace.isPending) return <LoadingState message="Reading the account's requests…" />;
  if (workspace.isError) {
    return <ErrorState title="Could not read this account" cause={errorMessage(workspace.error)} />;
  }

  const ws = workspace.data;
  const grace = ws.closure_grace;
  const intake = { channel, ticket_code: ticket.trim(), reason: '' };
  const noTicket = !intake.ticket_code;
  const awaiting = ws.requests.find((r) => r.kind === 'closure' && r.status === 'awaiting_identity');
  const fail = (error: unknown) => toast({ tone: 'error', message: errorMessage(error) });
  const fieldOptions = target === 'profile' ? PROFILE_FIELDS : FAMILY_FIELDS;
  const lastFour = (ws.subject.masked_phone ?? '').slice(-4);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-sm)',
          background: 'var(--c-info-bg)',
          color: 'var(--c-info-text)',
          border: '1px solid var(--c-info)',
          borderRadius: 'var(--r-tile)',
          padding: 'var(--sp-md) var(--sp-lg)',
          fontSize: 'var(--fs-admin-ui)',
        }}
      >
        <Icon name="shield" size={16} />
        <span style={{ flex: 1 }}>
          Assisted request for <span className="mono-num">{ws.subject.masked_phone ?? 'an anonymised account'}</span>
          {ws.subject.masked_name ? ` · ${ws.subject.masked_name}` : ''} · {ws.subject.status}. Users can
          self-serve export and closure in the app; this workspace exists for requests that arrive by
          phone, email, letter or the web form.
        </span>
        <Button size="sm" variant="secondary" icon="arrow-left" onClick={() => go('users')}>
          Back to account
        </Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 380px) minmax(0, 1fr)', gap: 'var(--sp-lg)', alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 'var(--sp-lg)' }}>
          <Section title="Request intake" note="A DSR without a ticket is refused">
            <Select density="admin" label="Request kind" value={kind} onChange={(v) => setKind(v as Kind)} options={KINDS} />
            <Select
              density="admin"
              label="Arrived on"
              value={channel}
              onChange={(v) => setChannel(v as AssistedChannel)}
              options={CHANNELS}
            />
            <Input density="admin" label="Linked ticket (required)" value={ticket} onChange={setTicket} placeholder="SUP…" />
            <KV
              rows={[
                ['Identity verification', 'A code to the registered phone, read back by the principal'],
                ['Verified by', 'The pipeline, with the principal'],
                ['Operator vouching', 'Not permitted'],
              ]}
            />
          </Section>

          {awaiting && (
            <Section title="Waiting for the principal's code" note="Sent to the registered phone — never to you">
              <Input density="admin" label="Code the principal read out" value={code} onChange={setCode} />
              <ScopeAction
                scope={scope}
                need="support"
                size="sm"
                icon="check"
                disabled={!code.trim()}
                onClick={() =>
                  verifyClosure.mutate(
                    { requestId: awaiting.id, code: code.trim() },
                    {
                      onSuccess: (r) => {
                        setCode('');
                        toast({
                          tone: r.status === 'blocked' ? 'info' : 'success',
                          message:
                            r.status === 'blocked'
                              ? `Identity verified · queued behind ${r.blockers?.length ?? 0} open matters`
                              : 'Identity verified · account closed, grace window running',
                        });
                      },
                      onError: fail,
                    },
                  )
                }
              >
                Verify code
              </ScopeAction>
            </Section>
          )}

          <Section title="Request history" note="Every request on this account, self-serve and assisted">
            {ws.requests.length === 0 ? (
              <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink3)' }}>No requests on this account yet.</p>
            ) : (
              <>
                <Timeline
                  events={ws.requests.map((r) => ({
                    title: `${r.kind} — ${r.status}`,
                    timestamp: formatDateTime(r.created_at),
                    detail: requestDetail(r),
                    state: requestState(r.status),
                  }))}
                />
                {ws.requests
                  .filter((r) => CANCELLABLE.has(r.status))
                  .map((r) => (
                    <ScopeAction
                      key={r.id}
                      scope={scope}
                      need="support"
                      size="sm"
                      variant="secondary"
                      icon="x"
                      onClick={() => setCancelling(r.id)}
                    >
                      {`Withdraw ${r.kind} #${r.id}`}
                    </ScopeAction>
                  ))}
              </>
            )}
          </Section>
        </div>

        <div style={{ display: 'grid', gap: 'var(--sp-lg)', minWidth: 0 }}>
          {kind === 'export' && (
            <Section
              title="Export"
              note="Every living data class: profile, family, addresses, bookings, consents, invoices, evidence index"
              actions={
                <ScopeAction scope={scope} need="support" size="sm" icon="package" disabled={noTicket} onClick={() => setConfirm('export')}>
                  Trigger export
                </ScopeAction>
              }
            >
              <div
                style={{
                  background: 'var(--c-sunk)',
                  border: '1px solid var(--c-border)',
                  borderRadius: 'var(--r-tile)',
                  padding: 'var(--sp-md)',
                  fontSize: 'var(--fs-admin-ui)',
                  color: 'var(--c-ink)',
                }}
              >
                The principal is told on their registered contact when it is ready and downloads it
                from the app. The archive is never shown here.
              </div>
              <KV
                rows={[
                  ['Delivery target', "The account's registered contact, then the app"],
                  ['Operator access to the archive', 'None — by design'],
                ]}
              />
            </Section>
          )}

          {kind === 'correction' && (
            <Section
              title="Correction on the user's behalf"
              note="Same validators as the devotee's own profile screen"
              actions={
                <ScopeAction
                  scope={scope}
                  need="support"
                  size="sm"
                  icon="check"
                  disabled={noTicket || !value.trim()}
                  onClick={() => setConfirm('correction')}
                >
                  Submit correction
                </ScopeAction>
              }
            >
              <Select
                density="admin"
                label="Record"
                value={target}
                onChange={(v) => {
                  setTarget(v);
                  setField(v === 'profile' ? 'gotra' : 'full_name');
                }}
                options={['profile', ...ws.family.map((f) => `family:${f.id}`)]}
              />
              {ws.family.length > 0 && (
                <KV rows={ws.family.map((f) => [`family:${f.id}`, `${f.relation} · ${f.masked_name ?? '—'}${f.is_deceased ? ' · deceased' : ''}`])} />
              )}
              <Select density="admin" label="Field" value={field} onChange={setField} options={fieldOptions} />
              <Input density="admin" label="Corrected value" value={value} onChange={setValue} />
              <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
                The audit log records that the field changed in redacted form, with your name, the
                ticket and the reason. The current value is never shown here.
              </p>
            </Section>
          )}

          {kind === 'closure' && (
            <>
              <Section title="What will be deleted" note="The scrub, then a verification pass that re-queries each class">
                <div style={{ display: 'grid', gap: 6, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>
                  {ws.deleted_classes.map((x) => (
                    <div key={x} style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
                      <Icon name="trash-2" size={14} style={{ color: 'var(--c-error)' }} />
                      {x}
                    </div>
                  ))}
                </div>
              </Section>

              <Section
                title="What will be retained regardless, and why"
                note="Statutory carve-outs · shown in full, always"
                pad={false}
              >
                <DataTable
                  density="compact"
                  columns={[
                    { key: 'cls', label: 'Data class' },
                    { key: 'retention', label: 'Retention' },
                    { key: 'covers', label: 'What it covers' },
                  ]}
                  rows={ws.retained_classes.map((c) => ({
                    id: c.data_class,
                    cls: c.data_class,
                    retention: c.retention,
                    covers: c.covers,
                  }))}
                  footer={
                    <>
                      <span>The devotee&rsquo;s confirmation states this same list.</span>
                      <Button size="sm" variant="ghost" icon="scale" onClick={() => go('ledger')}>
                        The money history survives in the ledger
                      </Button>
                    </>
                  }
                />
              </Section>

              <Section title="Blockers" note="A blocked request queues — it never fails silently">
                {ws.blockers.length === 0 ? (
                  <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink3)' }}>
                    No open bookings, refunds or disputes.
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
                    {ws.blockers.map((b) => (
                      <div
                        key={b.reference}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--sp-sm)',
                          padding: 'var(--sp-sm) var(--sp-md)',
                          background: 'var(--c-error-bg)',
                          border: '1px solid var(--c-error)',
                          borderRadius: 'var(--r-tile)',
                          fontSize: 'var(--fs-admin-ui)',
                          color: 'var(--c-error-text)',
                        }}
                      >
                        <Icon name="circle-alert" size={16} />
                        <span style={{ flex: 1 }}>
                          <strong style={{ fontWeight: 'var(--fw-semibold)' }}>{b.kind}</strong> —{' '}
                          <span className="mono-num">{b.reference}</span> · {b.status}
                        </span>
                        <Button size="sm" variant="secondary" onClick={() => go(BLOCKER_TARGET[b.kind]!)}>
                          Resolve
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <KV
                  rows={[
                    [
                      'Grace period',
                      `The scrub runs ${count(grace.days, 'day')} after closure; support can withdraw it until then.`,
                    ],
                    [
                      'Setting',
                      <span key="s" className="mono-num">
                        {grace.setting_key} = {grace.days}
                      </span>,
                    ],
                  ]}
                />
                <ScopeAction
                  scope={scope}
                  need="support"
                  size="sm"
                  variant="destructive"
                  icon="user-x"
                  disabled={noTicket || Boolean(awaiting) || ws.subject.status === 'closed'}
                  onClick={() => setConfirm('closure')}
                >
                  Queue closure
                </ScopeAction>
              </Section>
            </>
          )}
        </div>
      </div>

      <ConfirmAction
        open={confirm === 'export'}
        title="Trigger a data export?"
        phrase="EXPORT"
        tone="warn"
        reason
        consequences={[
          'The principal is told on their registered contact and downloads it from the app — never you.',
          'Assembly covers every living data class.',
          `The trigger, the delivery and every download are audited against ticket ${intake.ticket_code}.`,
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={(reason) => {
          setConfirm(null);
          requestExport.mutate(
            { ...intake, reason },
            {
              onSuccess: () => toast({ tone: 'success', message: 'Export queued · the principal is told when it is ready' }),
              onError: fail,
            },
          );
        }}
        confirmLabel="Trigger export"
      />

      <ConfirmAction
        open={confirm === 'correction'}
        title="Submit this correction on the user's behalf?"
        phrase="CORRECT"
        tone="warn"
        reason
        consequences={[
          `Changes ${field} on ${target}.`,
          'Records the ticket, the change in redacted form, and your name.',
          'Past bookings keep their snapshot — nothing in history moves.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={(reason) => {
          setConfirm(null);
          const change = { [field]: value.trim() };
          const body =
            target === 'profile'
              ? { ...intake, reason, profile: change }
              : { ...intake, reason, family: [{ id: Number(target.split(':')[1]), changes: change }] };
          applyCorrection.mutate(body, {
            onSuccess: () => {
              setValue('');
              toast({ tone: 'success', message: 'Correction recorded · audit event written' });
            },
            onError: fail,
          });
        }}
        confirmLabel="Submit correction"
      />

      <ConfirmAction
        open={confirm === 'closure'}
        title="Queue account closure?"
        /* The last four digits, so a closure cannot be queued against the wrong account. */
        phrase={lastFour}
        tone="error"
        reason
        consequences={[
          'Nothing closes yet: a code goes to the registered phone, and the principal must read it back.',
          'Financial records (10 years), tax records, consent history, audit events, open matters and the grievance register (3 years) are retained.',
          ws.blockers.length
            ? `${ws.blockers.length} open matters block execution — the request queues until they clear.`
            : 'No open matters — the account closes as soon as the code is verified.',
          'There is no hard delete: the account is anonymised in place.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={(reason) => {
          setConfirm(null);
          requestClosure.mutate(
            { ...intake, reason },
            {
              onSuccess: (r) => toast({ tone: 'info', message: `Code sent to ${r.code_sent_to} · ask the principal to read it back` }),
              onError: fail,
            },
          );
        }}
        confirmLabel="Queue closure"
      >
        <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>
          Confirm with the last four digits of the account&rsquo;s phone —{' '}
          <span className="mono-num">{ws.subject.masked_phone}</span> — so the closure cannot be queued
          against the wrong account.
        </p>
      </ConfirmAction>

      <ConfirmAction
        open={cancelling !== null}
        title="Withdraw this request?"
        phrase="WITHDRAW"
        tone="warn"
        reason
        consequences={[
          'The request stops. A closure in its grace window re-opens the account.',
          'Once the scrub has run there is nothing left to re-open, and the server refuses.',
        ]}
        onCancel={() => setCancelling(null)}
        onConfirm={(reason) => {
          const requestId = cancelling!;
          setCancelling(null);
          cancelDsr.mutate(
            { requestId, reason },
            { onSuccess: () => toast({ tone: 'success', message: 'Request withdrawn' }), onError: fail },
          );
        }}
        confirmLabel="Withdraw"
      />
    </>
  );
}
